import { FC, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  DocumentViewer,
  MiDragAndDropFileUpload,
  MiList,
  MiModalConfirm,
} from "@miplatform/components";
import {
  FILE_MIME_TYPE_EXTENSIONS,
  NOT_APPLICABLE,
} from "@miplatform/constants";
import {
  IAttempt,
  IDocumentCategoriesResponse,
  IStep,
} from "@miplatform/types";
import { mdiTrashCanOutline } from "@mdi/js";
import { DocumentCard } from "@mibuildweb/components";
import { Typography } from "@mui/material";
import { Box } from "@mui/system";
import { WorkOrderDrawerGeneralStep } from "../../components/WorkOrderDrawerGeneralStep";
import { useWorkOrderAttemptDocumentStepViewModel } from "./useWorkOrderAttemptDocumentStepViewModel";

export const WorkOrderAttemptDocumentStep: FC<
  WorkOrderAttemptDocumentStepProps
> = ({
  isViewMode,
  attempt,
  step,
  isLoading,
  documentCategories,
  onRefresh,
}) => {
  const { theme, classes } = useStyles();
  const {
    isDeleteModalOpen,
    selectedDocument,
    deleteFile,
    loading,
    uploadFiles,
    setDeleteDocument,
    printFile,
    deleteDocument,
    downloadFile,
    handleDocumentSelect,
    handleDeleteModalClose,
  } = useWorkOrderAttemptDocumentStepViewModel({
    attempt,
    step,
    onRefresh,
  });
  const allowedFileFormats = useMemo(() => {
    const documentCategory =
      documentCategories.find(
        (category) => category.id === step.documentCategoryId
      ) || documentCategories[0];

    return documentCategory?.allowedFileFormats || [];
  }, [documentCategories, step.documentCategoryId]);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {!!step.displayConfig.title && <WorkOrderDrawerGeneralStep step={step} />}
      {!isViewMode && (
        <Box className={classes.section} flexDirection="column" gap={2}>
          <Box display="flex" flexDirection="column">
            <Typography className={classes.title}>{step.title}</Typography>
            <Typography className={classes.description}>
              {step.description}
            </Typography>
          </Box>
          <MiDragAndDropFileUpload
            showUploadButton
            onChange={uploadFiles}
            disabled={isLoading || documentCategories.length === 0}
            validFileTypes={allowedFileFormats}
            acceptableFormatText={`Acceptable formats: ${
              allowedFileFormats
                ?.map((mimeType: string) => FILE_MIME_TYPE_EXTENSIONS[mimeType])
                .join(", ") || NOT_APPLICABLE
            }`}
          />
          <MiModalConfirm
            title="Delete Document"
            description={`Are you sure you want to delete document ${
              deleteFile?.metadata?.title || deleteFile?.filename
            }?`}
            titleIcon={{
              path: mdiTrashCanOutline,
              color: theme.palette.error.main,
            }}
            open={isDeleteModalOpen}
            disableClick={loading || isLoading}
            handlePositive={deleteDocument}
            handleNegative={handleDeleteModalClose}
          />
        </Box>
      )}
      {(step.stepResult?.documents?.length || 0) > 0 && (
        <MiList
          hideListPagination
          viewType="card"
          data={step.stepResult?.documents || []}
          CardComponent={DocumentCard}
          cardProps={{
            onPrint: printFile,
            onDelete: setDeleteDocument,
            onDownload: downloadFile,
            onSelect: handleDocumentSelect,
            selectedDoc: selectedDocument,
            disableDelete: isViewMode,
          }}
          loading={isLoading}
        />
      )}
      <DocumentViewer
        documents={[
          selectedDocument,
          ...(step.stepResult?.documents || []).filter(
            (d) => d.id !== selectedDocument?.id
          ),
        ]}
        onUnfullscreen={() => handleDocumentSelect(null)}
        fullScreenStatus={!!selectedDocument}
      />
    </Box>
  );
};

interface WorkOrderAttemptDocumentStepProps {
  isViewMode?: boolean;
  attempt: IAttempt | null;
  step: IStep;
  isLoading?: boolean;
  documentCategories: IDocumentCategoriesResponse[];
  onRefresh: () => void;
}

const useStyles = makeStyles()((theme) => ({
  section: {
    display: "flex",
    padding: theme.spacing(3),
    width: "100%",
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.grey.light,
    border: `1px solid ${theme.palette.grey.accent}`,
  },
  title: {
    fontWeight: theme.fontWeight.semiBold,
  },
  description: {
    color: theme.palette.grey.dark,
    fontSize: theme.typography.pxToRem(14),
  },
}));
